/**
 * Pathway domain shapes (PROD-642 Sprint 2.2 extraction).
 *
 * Pure-type module split out of `pathway.store.ts` so non-store
 * consumers (FHIR mapper, demo seed, read-through cache) can reference
 * the in-memory pathway domain without dragging the Zustand store into
 * their import graph. The store re-exports these names so existing
 * callers keep working.
 *
 * HC5: zero em / en dashes.
 */
import type { LabBasketItem } from '../types/lab-basket';
import type {
  ActionCategory,
  PathwayNodeType,
  StepCategory,
} from '../services/pathway-layout';
import type { PathwayId } from './pathway-ids';

export interface PathwayNode {
  id: string;
  nodeType: PathwayNodeType;
  label: string;
  /**
   * `requiresConfirmation` is the lab-basket-level flag the editor
   * surfaces per ticket AC. Only meaningful for `nodeType === 'labs'`
   * and `nodeType === 'decision'`; ignored otherwise.
   */
  requiresConfirmation?: boolean;
  /** State-machine description for Decision Point nodes. */
  stateMachine?: string;
  /** NBA candidate ids linked to a Decision Point. */
  linkedNbaIds?: string[];
  /** Lab basket items for a Labs node. */
  labBasketItems?: LabBasketItem[];
  /** Free-form label-extension text shown in the inspector. */
  description?: string;
  /**
   * Sprint 4.3.1 (PROD-650): Awell 3-tier hierarchy.
   * `parentNodeId` points to the containing parent. For a `step` node,
   * the parent is the enclosing `track`; for an `action` node, the
   * parent is the enclosing `step`. `track` nodes are top-level so they
   * never carry a parent.
   *
   * Reference: `awell/studio/docs/getting-started/design-ontology.md`.
   */
  parentNodeId?: string;
  /**
   * Discriminator for `nodeType === 'action'` per Awell ontology.
   * `'user'` actions require a real stakeholder to complete (e.g. fill
   * a form); `'system'` actions are automatically executed (calculation,
   * API call). Ignored on non-action nodes.
   */
  category?: ActionCategory;
  /**
   * Track entry trigger description (declarative event + optional
   * filter expression). Only meaningful for `nodeType === 'track'`.
   */
  triggerType?: string;
  triggerFilter?: string;
  /**
   * Decision-branch flag for `nodeType === 'step'`. When `true` the
   * step renders as a diamond with multi-out transitions per the
   * Awell ontology decision branch shape.
   */
  isDecision?: boolean;
  /**
   * Sprint 4.3.5: Step category surfaced as a coloured pill badge on the
   * canvas. Closed vocabulary `baseline | program | exclusion | completion
   * | review | other`. Optional; when absent the badge is hidden. Distinct
   * from `category` (user/system action discriminator).
   */
  stepCategory?: StepCategory;
  /**
   * Display label for the step category pill (e.g. "BASELINE", "16 Week
   * Program"). When absent the renderer falls back to the uppercased
   * `stepCategory` value. Free-form per fixture; HC5 zero em / en dashes.
   */
  stepCategoryLabel?: string;
  /**
   * Optional duration text for `stepCategory === 'program'` rendered in
   * place of `stepCategoryLabel` (e.g. "6 Week Program", "16 Week
   * Program"). Ignored for other categories.
   */
  programDuration?: string;
  /**
   * Sprint 4.3.5: pre-computed stakeholder count surfaced in the
   * StepStatsRow at the bottom of a Step card. Optional; defaults to 0
   * when absent. Fixtures may set this directly, or a future extractor
   * can derive it from action-level stakeholder refs.
   */
  stakeholderCount?: number;
  /**
   * Sprint 4.3.5: pre-computed location count for the StepStatsRow.
   * Optional; defaults to 0.
   */
  locationCount?: number;
  /**
   * Sprint 4.3.8: id of the stakeholder assigned to this node. Only
   * meaningful for `nodeType === 'action'` per Awell domain model file
   * 03 (the `indirect_object` field on the activity carries the assignee
   * identity at runtime). Resolved against `useStakeholdersStore`.
   */
  assignedStakeholderId?: string;
  /**
   * Sprint 4.3.9: native activity-type discriminator for `nodeType ===
   * 'action'`. Mirrors the Awell six native activity types per the vault
   * note `domains/clinical/pathway-engineering/six-native-activity-types
   * .md`. When `actionType === 'form'`, the inspector renders the form
   * question authoring panel and `formQuestions` is the editable list.
   * Ignored on non-action nodes.
   */
  actionType?: ActionType;
  /**
   * Sprint 4.3.9: ordered list of questions authored on a form-type
   * action. Each entry binds to an optional `data_point_definition` id
   * via `dataPointDefinitionId` so a runtime answer lands on a typed
   * data point per the vault note `domains/clinical/pathway-engineering
   * /data-point-definition-vs-data-point.md`. Only meaningful when
   * `actionType === 'form'`.
   */
  formQuestions?: FormQuestion[];
  /**
   * Sprint 4.3.10: optional transition / step-advancement rule. Only
   * meaningful for `nodeType === 'step'`. When present, the rule guards
   * the step's outbound transitions per the vault note
   * `domains/clinical/pathway-engineering/conditional-logic-and-rules.md`
   * (Step advancement evaluation point).
   */
  transitionRule?: PathwayRule;
}

/**
 * Sprint 4.3.9: Awell native activity types. The six values come from
 * the vault note `domains/clinical/pathway-engineering/
 * six-native-activity-types.md`. The discriminator decides which
 * inspector sub-panel renders for an `action` node.
 */
export const ACTION_TYPES = [
  'form',
  'calculation',
  'message',
  'api_call',
  'reminder',
  'checklist',
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

/**
 * Sprint 4.3.9: closed vocabulary of question types surfaced on a form
 * action. Maps onto FHIR `QuestionnaireItem.type` per the round-trip
 * mapper in `pathway-to-plan-definition.ts`.
 *
 *   text           -> string
 *   number         -> decimal
 *   date           -> date
 *   single_choice  -> choice (repeats=false)
 *   multiple_choice-> choice (repeats=true)
 *   scale          -> integer (range encoded in min/max)
 */
export const FORM_QUESTION_TYPES = [
  'text',
  'number',
  'date',
  'single_choice',
  'multiple_choice',
  'scale',
] as const;
export type FormQuestionType = (typeof FORM_QUESTION_TYPES)[number];

/**
 * Sprint 4.3.9: choice option for `single_choice` / `multiple_choice`
 * question types. `value` is the canonical machine value persisted in
 * the answer payload; `label` is the user-facing surface in the form.
 */
export interface FormQuestionOption {
  value: string;
  label: string;
}

/**
 * Sprint 4.3.9: single editable question on a form action node.
 *
 *   id                       stable per-question identifier.
 *   text                     the question label rendered to the user.
 *   type                     closed vocab from FORM_QUESTION_TYPES.
 *   required                 true when the answer is mandatory.
 *   options                  enum for choice types; ignored otherwise.
 *   dataPointDefinitionId    optional canonical id of the
 *                            `data_point_definition` the answer binds to.
 *   scaleMin / scaleMax      integer range for `type === 'scale'`.
 *   enableWhenRule           Sprint 4.3.10: optional conditional-display
 *                            rule. When present, the question is only
 *                            shown if the rule evaluates to true against
 *                            the current data points (mirrors FHIR
 *                            `Questionnaire.item.enableWhen`).
 */
export interface FormQuestion {
  id: string;
  text: string;
  type: FormQuestionType;
  required: boolean;
  options?: FormQuestionOption[];
  dataPointDefinitionId?: string;
  scaleMin?: number;
  scaleMax?: number;
  enableWhenRule?: PathwayRule;
}

/**
 * Sprint 4.3.10: closed vocabulary of rule operators. Mirrors the
 * Awell rule operator set augmented with the FHIR `enableWhen.operator`
 * vocabulary so a rule round-trips through both `PlanDefinition.action.
 * condition` (transitions) and `Questionnaire.item.enableWhen` (form
 * question display) without lossy coercion.
 *
 * FHIR R4 native enableWhen operators: `exists | = | != | > | < | >= | <=`.
 * The `in` and `not-in` operators are Scrips extensions that fall back to
 * a CPS extension on the FHIR side and are encoded as a serialized
 * FHIRPath-style string on the PlanDefinition condition expression.
 */
export const RULE_OPERATORS = [
  'equals',
  'not-equals',
  'greater-than',
  'less-than',
  'greater-than-or-equals',
  'less-than-or-equals',
  'in',
  'not-in',
  'is-empty',
  'is-not-empty',
] as const;
export type RuleOperator = (typeof RULE_OPERATORS)[number];

/**
 * Sprint 4.3.10: closed Scrips risk-tier vocabulary. Captured on a rule
 * to flag a clinical-risk-bearing branch per the vault note
 * `domains/clinical/pathway-engineering/conditional-logic-and-rules.md`
 * (Scrips-Distinct Contribution section). The `elevated` and `critical`
 * tiers route the rule evaluation through a supervisor review task at
 * runtime; `standard` is the default no-op tier. The runtime task
 * surface is OUT OF SCOPE for this sprint; the editor + serializer
 * preserve the flag end-to-end so a future runtime can act on it.
 */
export const RULE_RISK_TIERS = ['standard', 'elevated', 'critical'] as const;
export type RuleRiskTier = (typeof RULE_RISK_TIERS)[number];

/**
 * Sprint 4.3.10: a single-condition rule. Single-operand, single-operator
 * by design (multi-condition AND / OR composition is a future sprint).
 *
 *   id                      stable per-rule identifier.
 *   name                    free-form display label (e.g. "ldl-above
 *                           -threshold").
 *   dataPointDefinitionId   canonical id of the operand data point
 *                           definition. Every rule operand binds to a
 *                           `data_point_definition`.
 *   operator                closed vocab from RULE_OPERATORS.
 *   value                   right-hand operand. `string | number` for
 *                           the comparison operators, `string[]` for
 *                           `in` / `not-in`. Ignored for `is-empty` /
 *                           `is-not-empty`.
 *   riskTier                optional Scrips risk tier. Absent = standard.
 */
export interface PathwayRule {
  id: string;
  name: string;
  dataPointDefinitionId: string;
  operator: RuleOperator;
  value?: string | number | string[];
  riskTier?: RuleRiskTier;
}

export interface PathwayEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface EditablePathway {
  id: string;
  pathwayId: PathwayId;
  tenantId: string;
  /**
   * Sprint 4.3.7-fix: optional user-authored name. Set by `createPathway`
   * with the value the user typed in the New Pathway dialog. The header
   * Pathway picker prefers this label over the hardcoded
   * `PATHWAY_DISPLAY_NAMES` lookup so user-created pathways surface in the
   * dropdown by their actual name (founder direction 2026-05-08).
   */
  name?: string;
  /** Sprint 4.3.7-fix: free-form description captured at creation. */
  description?: string;
  nodes: PathwayNode[];
  edges: PathwayEdge[];
}

/** Patch shape accepted by `editNode`. */
export interface NodePatch {
  label?: string;
  requiresConfirmation?: boolean;
  stateMachine?: string;
  description?: string;
  category?: ActionCategory;
  triggerType?: string;
  triggerFilter?: string;
  isDecision?: boolean;
  stepCategory?: StepCategory;
  stepCategoryLabel?: string;
  programDuration?: string;
  stakeholderCount?: number;
  locationCount?: number;
  parentNodeId?: string;
  /**
   * Sprint 4.3.8: stakeholder assignment for action nodes. Threading the
   * field through the patch shape lets the inspector + tests commit a
   * stakeholder selection through the existing `editNode` surface.
   */
  assignedStakeholderId?: string;
  /**
   * Sprint 4.3.9: action-type discriminator for `nodeType === 'action'`.
   * The inspector toggles the form authoring panel when this is `'form'`.
   */
  actionType?: ActionType;
  /**
   * Sprint 4.3.9: full replacement list of authored form questions.
   * `editNode` accepts the patch as a list-replace because question
   * add / remove / reorder is naturally handled in the inspector.
   */
  formQuestions?: FormQuestion[];
  /**
   * Sprint 4.3.10: transition / step-advancement rule patch. `null`
   * clears the rule; an object replaces it. The store treats any
   * `transitionRule` key in the patch as a real change so the inspector
   * can clear or replace the rule via a single `editNode` call.
   */
  transitionRule?: PathwayRule | null;
}

/**
 * Optional UI-supplied position used by the canvas to lay out a freshly
 * dropped node. The canvas resolves the drop coordinate via React Flow's
 * `screenToFlowPosition` and threads it through `addNode` so the new
 * node renders at the user-intended location.
 */
export interface NodePosition {
  x: number;
  y: number;
}

export type PathwayEditType =
  | 'node_property'
  | 'lab_basket_preselected'
  | 'lab_basket_requires_confirmation'
  | 'lab_basket_rationale'
  | 'lab_basket_item';
